/**
 * How the patient pages write appointment times and the clinic phone, shared by booking and the
 * manage page. A time zone of undefined means the device's own (a manage session from a server
 * before PA-9 names no clinic time zone).
 */

export function formatDateTime(value: string, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone === undefined ? {} : { timeZone }),
  }).format(new Date(value));
}

export function formatTime(value: string, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat("en-CA", {
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone === undefined ? {} : { timeZone }),
  }).format(new Date(value));
}

/** Whether the device shows this instant at the same clock time as the clinic. */
export function sameClockAsDevice(value: string, timeZone: string | undefined): boolean {
  return timeZone === undefined || formatTime(value, timeZone) === formatTime(value, undefined);
}

export function formatDateTimeRange(
  startAt: string,
  endAt: string,
  timeZone: string | undefined,
): string {
  return `${formatDateTime(startAt, timeZone)} – ${formatTime(endAt, timeZone)}`;
}

/** "Tuesday, October 20" for a local date (YYYY-MM-DD), whatever the device's time zone. */
export function formatLocalDate(date: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00Z`));
}

/**
 * "March 14, 1990" for a date of birth (YYYY-MM-DD), whatever the device's time zone; anything
 * that is not such a date is shown as typed.
 */
export function formatBirthDate(date: string): string {
  const day = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T12:00:00Z`) : null;
  if (day === null || Number.isNaN(day.getTime())) return date;
  return new Intl.DateTimeFormat("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(day);
}

export function slotDurationMinutes(startAt: string, endAt: string): number {
  return Math.max(1, Math.round((Date.parse(endAt) - Date.parse(startAt)) / 60_000));
}

const localDateFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * The local date (YYYY-MM-DD) of an instant on a clock: the clinic's, or the device's. The
 * calendar dates every time in a 63-day search, so each clock's formatter is made once.
 */
export function localDateOf(value: string | Date, timeZone: string | undefined): string {
  const key = timeZone ?? "";
  let format = localDateFormats.get(key);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      ...(timeZone === undefined ? {} : { timeZone }),
    });
    localDateFormats.set(key, format);
  }
  const parts = format.formatToParts(typeof value === "string" ? new Date(value) : value);
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/**
 * A dialable tel: address for the site phone as the clinic typed it. Any extension stays in the
 * visible text only; null when there is no number to dial.
 */
export function telephoneHref(phone: string): string | null {
  const [number = ""] = phone.split(/\s*(?:ext\.?|extension|x)\s*\d/i, 1);
  if (/[a-z]/i.test(number)) return null;
  const dialable = number.trim().replace(/(?!^\+)\D/g, "");
  return /\d{3,}/.test(dialable) ? `tel:${dialable}` : null;
}
